import { FormatOptions } from "./format";
import { getActiveDateTimeFormat } from "./registry";

/**
 * Convenience formatters that read the active date/time preference from the
 * registry. Use these in pure (non-React) code; React components should prefer
 * the `useDateTimeFormat` hook so they re-render when the preference changes.
 *
 * All accept a `Date`, an epoch-millisecond number, or a date string / ISO
 * value (anything `new Date(...)` understands). Invalid inputs return the
 * `fallback` (default `""`) so a single bad value can't throw inside a table
 * cell or chart axis.
 */

export type DateInput = Date | string | number | null | undefined;

/** Coerce a {@link DateInput} into a valid `Date`, or `null`. */
export function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatWith(
  value: DateInput,
  options: FormatOptions,
  fallback: string,
): string {
  const date = toDate(value);
  if (date === null) {
    return fallback;
  }
  return getActiveDateTimeFormat().format(date, options);
}

/** Date only, honoring the active preset (e.g. `30.06.2026`). */
export function formatDate(value: DateInput, fallback = ""): string {
  return formatWith(value, { time: "none" }, fallback);
}

/** Date + `HH:mm`, honoring the active preset and clock. */
export function formatDateTime(value: DateInput, fallback = ""): string {
  return formatWith(value, { time: "minutes" }, fallback);
}

/** Date + `HH:mm:ss`. */
export function formatDateTimeWithSeconds(
  value: DateInput,
  fallback = "",
): string {
  return formatWith(value, { time: "seconds" }, fallback);
}

/**
 * Escape hatch for callers that need full control over the granularity and
 * timezone display in one call — including millisecond precision
 * (`{ time: "millis" }`), which has no named helper.
 */
export function formatDateTimeWithOptions(
  value: DateInput,
  options: FormatOptions,
  fallback = "",
): string {
  return formatWith(value, options, fallback);
}
